import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Route, RouteResponse } from 'src/app/models/route.model';
import { RoutesService } from 'src/app/services/routes.service';
import { Loader } from '@googlemaps/js-api-loader';
import { GlobalConstants } from 'src/app/utils/global-constants';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-map-view',
    standalone: false,
    templateUrl: './map-view.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './map-view.component.scss'
})
export class MapViewComponent implements OnInit, OnDestroy {
    @ViewChild('googleMap', { static: true })
    routeKeys: any[] = [];
    routeKeys2: any = {};
    unsubscriber$: Subject<any> = new Subject();

    googleMap!: any;

    map!: google.maps.Map;
    mapLoaded: boolean = false;
    loadingRoutes: boolean = true;
    routes!: RouteResponse;
    routesDisplayed: any[] = [];
    namespace: String = "";
    myRoutes: String = "";

    renderers: any = [];
    visibility: any = [];

    directions: any;

    constructor(
        private router: Router,
        private activeRoute: ActivatedRoute,
        private routeService: RoutesService,
    ) { }

    ngOnDestroy(): void {
        this.unsubscriber$.unsubscribe();
    }

    ngOnInit(): void {
        this.routeKeys = [
            "origin",
            "destination",
            "startTime",
            "distance",
            "totalTime",
            "idealTotalTime",
            "isOptimized",
            "waypoints"
        ]

        this.routeKeys2 = {
            "origin": "Origin",
            "destination": "Destination",
            "startTime": "Start Time",
            "distance": "Distance",
            "totalTime": "Total Time",
            "idealTotalTime": "Ideal Total Time",
            "isOptimized": "Is Optimized",
            "waypoints": "Waypoints"
        }

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                const currentUrl = this.router.url;
                const urlTree = this.router.parseUrl(currentUrl);
                const urlParams = urlTree.root.children['primary'].segments;

                this.namespace = urlParams[1]?.path;
                this.myRoutes = urlParams[2]?.path;

                this.routeService.getRoutes(this.namespace, this.myRoutes).subscribe((response) => {
                    this.routes = response;

                    for (let i = 0; i < this.routes.items.length; i++) {
                        (this.routes.items[i] as any)['visibility'] = true;
                        (this.routes.items[i] as any)['isExpanded'] = false;
                    }

                    // Añadimos la letra a los waypoints
                    this.routes.items.forEach((route: any) => {
                        if (route.waypoints && route.waypoints.length) {
                            route.waypoints = route.waypoints.map((wp: any, idx: number) => ({
                                ...wp,
                                letter: String.fromCharCode(66 + idx) //66 es el codigo ASCII de B
                            }));
                        }
                    });

                    this.routesDisplayed = this.routes.items;
                    this.loadingRoutes = false;
                    this.mapLoaded = true;

                    const loader = new Loader({
                        apiKey: environment.mapsApiKey,
                        version: 'weekly',
                    });

                    loader.importLibrary('maps')
                        .then(({ Map }) => {
                            this.map = new Map(document.getElementById("routes-map") as HTMLElement, {
                                zoom: 30,
                                center: { lat: -3.6850321, lng: -37.17351695 },
                            });
                            this.initMap();
                        })
                        .catch((e) => {
                            console.error('Error loading Google Maps:', e);
                        });
                });
            }
        });
    }

    initMap(): void {
        for (let i = 0; i < this.routes.items.length; i++) {
            const halo = new google.maps.DirectionsRenderer({
                map: this.map,
                suppressMarkers: true,
                polylineOptions: {
                    strokeColor: '#FFFFFF',
                    strokeOpacity: 1,
                    strokeWeight: 8,
                    zIndex: 1
                }
            });

            const jsonObject = JSON.parse(this.routes.items[i]['myDirectionJSON']);
            const directionsResult: google.maps.DirectionsResult = jsonObject as google.maps.DirectionsResult;

            halo.setDirections(directionsResult);

            const line = new google.maps.DirectionsRenderer({
                map: this.map,
                suppressMarkers: false,
                polylineOptions: {
                    strokeColor: GlobalConstants.colors[i % GlobalConstants.colors.length],
                    strokeOpacity: 0.9,
                    strokeWeight: 6,
                    zIndex: 2
                }
            });

            line.setDirections(directionsResult);

            this.renderers.push({ halo, line });
            this.visibility.push(true);
        }
    }

    toggleRouteExpansion(index: number): void {
        (this.routes.items[index] as any)['isExpanded'] = !(this.routes.items[index] as any)['isExpanded'];
    }

    getRouteExpanded(index: number): boolean {
        return (this.routes.items[index] as any)['isExpanded'] || false;
    }

    changeVisibility(index: number): void {
        let currentVisibility = this.visibility[index];

        if (currentVisibility) {
            this.renderers[index].halo.setMap(null);
            this.renderers[index].line.setMap(null);
        } else {
            this.renderers[index].halo.setMap(this.map);
            this.renderers[index].line.setMap(this.map);
        }

        this.visibility[index] = !currentVisibility;
        this.routes.items[index]['visibility'] = !this.routes.items[index]['visibility'];
    }

    getFormattedTime(seconds: number, noLetter?: boolean): string {
        let hours = Math.floor(seconds / 3600);
        let minutes = Math.floor((seconds % 3600) / 60);

        if (noLetter) {
            let time = minutes;
            if (hours > 0)
                time = hours + minutes;

            return time.toString();
        }
        let time = minutes + "m";
        if (hours > 0)
            time = hours + "h " + minutes + "m";

        return time;
    }

    getFormattedDistance(meters: number): string {
        return (meters / 1000).toFixed(2) + " km"
    }

    getRouteColor(index: number): any {
        return GlobalConstants.colors[index % GlobalConstants.colors.length];
    }

    formatDateString(dateString: string): string {
        const date = new Date(dateString);
        return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }

    navigateTo(url: string): void {
        window.open(url, "_blank");
    }

    getDestinationLetter(route: any): string {
        const waypointCount = route.waypoints ? route.waypoints.length : 0;
        return String.fromCharCode(65 + waypointCount);
    }
}